import React from 'react'
import { Popup } from '@/ui/overlays/Popup'
import { Button } from '@/ui/button'
import { DriverPicker } from '@/modules/shared/components/driver-picker/DriverPicker'
import { Driver } from '@/lib/api/company/drivers/types'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { toast } from '@/lib/toasts/api'
import useBusinessTripService, {
  OverlappingTrip,
} from '@/modules/drivers/services/BusinessTripService'
import moment from 'moment'

type Props = {
  open: boolean
  companyId: string
  tripIds: number[]
  onClose: () => void
  onCopied: () => void
}

// Serwer oddaje juz czas lokalny, jak reszta API - parsujemy bez strefy.
// Format jak w ostrzezeniu WPF (CopyTripCommandExecute).
function formatTripDate(value: string) {
  const parsed = moment(value)
  return parsed.isValid() ? parsed.format('DD.MM.YYYY HH:mm') : value
}

const CopyTripsToDriverModal: React.FC<Props> = ({
  open,
  companyId,
  tripIds,
  onClose,
  onCopied,
}) => {
  const { t } = useLocalize()
  const { copyBusinessTrips } = useBusinessTripService()

  const [pendingDriver, setPendingDriver] = React.useState<Driver | null>(null)
  const [overlapping, setOverlapping] = React.useState<OverlappingTrip[]>([])
  const [isCopying, setIsCopying] = React.useState(false)

  const reset = React.useCallback(() => {
    setPendingDriver(null)
    setOverlapping([])
    setIsCopying(false)
  }, [])

  const handleClose = React.useCallback(() => {
    reset()
    onClose()
  }, [reset, onClose])

  const runCopy = React.useCallback(
    async (driver: Driver, ignoreOverlapping: boolean) => {
      setIsCopying(true)
      try {
        const result = await copyBusinessTrips({
          companyId,
          ids: tripIds,
          destinationDriverId: driver.id,
          ignoreOverlapping,
        })

        // Serwer nie kopiuje nic, dopoki uzytkownik nie potwierdzi nachodzacych
        // delegacji u kierowcy docelowego - tak samo jak ostrzezenie w WPF.
        if (result.copied === 0 && result.overlapping.length > 0) {
          setPendingDriver(driver)
          setOverlapping(result.overlapping)
          return
        }

        toast.success({
          text: t('businessTrip.trips-copied', {
            count: String(result.copied),
            driver: result.driverFullName,
          }),
        })
        onCopied()
        handleClose()
      } catch (error) {
        console.error('Error copying business trips:', error)
        toast.error({ text: t('error.copy-failed') })
      } finally {
        setIsCopying(false)
      }
    },
    [companyId, tripIds, copyBusinessTrips, t, onCopied, handleClose],
  )

  if (!open) return null

  if (overlapping.length > 0 && pendingDriver) {
    return (
      <Popup open onClose={handleClose}>
        <div className="tw-flex tw-max-w-[560px] tw-flex-col tw-gap-4 tw-p-6">
          <h2 className="tw-text-lg tw-font-semibold">
            {t('businessTrip.copy-overlapping-title')}
          </h2>
          <p className="tw-text-sm">
            {t('businessTrip.copy-overlapping-message', {
              driver: pendingDriver.fullName ?? '',
            })}
          </p>
          <ul className="tw-max-h-[240px] tw-overflow-auto tw-rounded tw-bg-gray-50 tw-p-3 tw-text-sm">
            {overlapping.map((trip) => (
              <li key={trip.id} className="tw-py-0.5">
                {trip.tripNumber}, {t('businessTrip.delegation-start-date')}:{' '}
                {formatTripDate(trip.startDate)},{' '}
                {t('businessTrip.delegation-end-date')}:{' '}
                {formatTripDate(trip.endDate)}
              </li>
            ))}
          </ul>
          <div className="tw-flex tw-justify-end tw-gap-2">
            <Button
              text={t('common.cancel')}
              variant="neutral"
              mode="outline"
              size="sm"
              onClick={handleClose}
            />
            <Button
              text={t('common.confirm')}
              variant="primary"
              size="sm"
              loading={isCopying}
              onClick={() => runCopy(pendingDriver, true)}
            />
          </div>
        </div>
      </Popup>
    )
  }

  return (
    <Popup open onClose={handleClose}>
      <DriverPicker
        companyId={companyId}
        multiple={false}
        disabled={isCopying}
        onCancel={handleClose}
        onSubmit={(selected) => {
          if (selected[0]) runCopy(selected[0], false)
        }}
        title={t('businessTrip.copy-select-driver')}
        submitLabel={t('businessTrip.copy')}
        cancelLabel={t('common.cancel')}
      />
    </Popup>
  )
}

export default CopyTripsToDriverModal
